import { useEffect, useState } from "react";
import { api, fmtAUD, fmtDate, formatApiError } from "@/lib/api";
import { Megaphone, Plus, Edit2, Trash2, AlertTriangle } from "lucide-react";
import { Pill } from "@/components/badges";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Active", "Paused", "Ended"];
const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Paused: "bg-amber-50 text-amber-700 border border-amber-200",
  Ended: "bg-slate-100 text-slate-600 border border-slate-200",
};

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setItems(data);
      const map = {};
      await Promise.all(data.map(async (c) => {
        try { const r = await api.get(`/campaigns/${c.id}/usage`); map[c.id] = r.data; }
        catch { map[c.id] = { lead_count: 0, opp_count: 0, total: 0 }; }
      }));
      setUsage(map);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/campaigns/${toDelete.id}`);
      toast.success("Campaign removed");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8" data-testid="campaigns-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyb-label">Marketing</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Campaigns</h1>
          <p className="text-sm text-slate-500 mt-1">Track marketing campaigns. Tag leads to a campaign and the link carries through when converting to opportunities.</p>
        </div>
        <Button data-testid="new-campaign-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
          <Plus className="w-4 h-4 mr-1" /> New Campaign
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
        {loading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="col-span-3 eyb-card p-12 text-center">
            <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No campaigns yet. Click <span className="font-semibold">New Campaign</span> to create one.</p>
          </div>
        ) : items.map((c) => {
          const u = usage[c.id] || { lead_count: 0, opp_count: 0, total: 0 };
          const inUse = u.total > 0;
          return (
            <div key={c.id} className="eyb-card p-5" data-testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-slate-900 truncate">{c.name}</h3>
                    <Pill className={STATUS_STYLES[c.status] || STATUS_STYLES.Ended}>{c.status}</Pill>
                  </div>
                  {c.description && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.description}</p>}
                  <div className="text-[11px] text-slate-500 mt-2.5 space-y-0.5">
                    {(c.start_date || c.end_date) && (
                      <div>{c.start_date ? fmtDate(c.start_date) : "—"} → {c.end_date ? fmtDate(c.end_date) : "—"}</div>
                    )}
                    {c.budget != null && <div>Budget: <span className="font-semibold text-slate-700">{fmtAUD(c.budget)}</span></div>}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[11px]">
                    <Pill className="bg-sky-50 text-sky-700 border border-sky-200">{u.lead_count} lead{u.lead_count === 1 ? "" : "s"}</Pill>
                    <Pill className="bg-violet-50 text-violet-700 border border-violet-200">{u.opp_count} opp{u.opp_count === 1 ? "" : "s"}</Pill>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`campaign-edit-${c.id}`}
                    onClick={() => { setEditing(c); setShowForm(true); }}
                    className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`campaign-delete-${c.id}`}
                    onClick={() => setToDelete({ ...c, usage: u })}
                    disabled={inUse}
                    className={`p-2 rounded-md ${inUse ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:text-rose-600 hover:bg-rose-50"}`}
                    title={inUse ? "Cannot delete: still in use" : "Delete"}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <CampaignFormModal
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="campaign-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Delete campaign?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? (
                toDelete.usage?.total > 0
                  ? <>This campaign is tagged on {toDelete.usage.lead_count} lead(s) and {toDelete.usage.opp_count} opportunity(ies). Untag them first.</>
                  : <>Permanently remove <span className="font-semibold">{toDelete.name}</span>?</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={!!toDelete && toDelete.usage?.total > 0}
              data-testid="campaign-delete-action"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CampaignFormModal({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    description: initial?.description || "",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    status: initial?.status || "Active",
    budget: initial?.budget ?? "",
  }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after start date"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status,
        budget: form.budget === "" ? null : Number(form.budget),
      };
      if (editing) await api.patch(`/campaigns/${initial.id}`, payload);
      else await api.post("/campaigns", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="campaign-form-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#29ABE2]/10 text-[#187199] flex items-center justify-center">
              <Megaphone className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>{editing ? "Edit campaign" : "New campaign"}</DialogTitle>
              <DialogDescription>Track a marketing initiative end-to-end from lead capture through closing.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="camp-name">Name</Label>
            <Input id="camp-name" data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="camp-desc">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <textarea id="camp-desc" data-testid="campaign-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="camp-start">Start date</Label>
              <Input id="camp-start" data-testid="campaign-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-end">End date</Label>
              <Input id="camp-end" data-testid="campaign-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="camp-status">Status</Label>
              <select id="camp-status" data-testid="campaign-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-budget">Budget (AUD)</Label>
              <Input id="camp-budget" data-testid="campaign-budget-input" type="number" min="0" step="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="0" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="campaign-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#1B4F8B] hover:bg-[#163e6e]" data-testid="campaign-form-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
